import React, { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Building2, Store, Warehouse, Layers, AlertTriangle, Package, MapPin, Mail, Star, Shield, Clock, CheckCircle, Activity, DollarSign, Phone, Loader2 } from "lucide-react";
import { fadeUp, Section, Card, GoldButton } from "../components/shared/UIComponents";
import { AnimatedCounter } from "../components/shared/AnimatedCounter";
import TexasMarketsMap from "../components/landing/TexasMarketsMap";
import { useAuth } from "../context/AuthContext";
import { usePageNavigate } from "../hooks/usePageNavigate";

const HERO_IMG = "https://images.unsplash.com/photo-1692154600992-463fa9b27abd?auto=format&fit=crop&w=2000&q=80";

const ACQUISITION_TYPES = [
  { icon: Building2, title: "Multifamily", desc: "5 to 500+ units. Value-add, workforce, and stabilized. Our primary asset class." },
  { icon: Store, title: "Retail", desc: "Strip centers, single tenant NNN, and anchored retail." },
  { icon: Warehouse, title: "Industrial", desc: "Flex, warehouse, and light industrial." },
  { icon: Layers, title: "Mixed-Use", desc: "Ground floor commercial with residential above." },
  { icon: AlertTriangle, title: "Distressed & Vacant", desc: "Underperforming assets with repositioning potential." },
  { icon: Package, title: "Portfolio Sales", desc: "Multiple properties sold together. We simplify complex transactions." },
];

const TESTIMONIALS = [
  { name: "Marcus Williams", role: "Multifamily Owner — Houston TX", quote: "Imaani made selling my 24-unit apartment complex simple. Offer in 48 hours, closed in 18 days. No games." },
  { name: "Jennifer Alvarez", role: "Capital Partner — Dallas TX", quote: "Deployed capital with Imaani on a DFW retail acquisition. Professional team, strong execution." },
  { name: "Sarah Chen", role: "Commercial Broker — Austin TX", quote: "Best off-market buyer I've worked with in Texas. They close what they say they'll close." },
  { name: "David Okafor", role: "Investor — Phoenix AZ", quote: "Imaani's underwriting gave me full confidence in the deal. Exactly what I was looking for in a capital partner." },
];

export default function HomePage() {
  const { navigateTo } = usePageNavigate();
  const { user } = useAuth();

  return (
    <>
      {/* ═══ HERO ═══ */}
      <section className="relative min-h-[60vh] sm:min-h-[75vh] lg:min-h-[90vh] overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0">
          <motion.img src={HERO_IMG} alt="Houston Texas skyline" className="h-full w-full object-cover object-center"
            initial={{ scale: 1.12 }} animate={{ scale: 1 }} transition={{ duration: 18, ease: "easeOut" }} />
          <div className="absolute inset-0 bg-gradient-to-r from-emerald-950/95 via-emerald-950/85 to-emerald-950/70 sm:via-emerald-950/80 sm:to-emerald-950/60" />
          <div className="absolute inset-0 bg-gradient-to-t from-emerald-950 via-transparent to-emerald-950/40" />
          <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M30 0L60 30L30 60L0 30z' fill='none' stroke='%23E5C98B' stroke-width='0.5'/%3E%3C/svg%3E")`, backgroundSize: '60px 60px' }} />
        </div>
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/4 left-0 w-[500px] h-[500px] bg-emerald-500/10 rounded-full blur-[150px]" />
          <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-gold/10 rounded-full blur-[120px]" />
        </div>

        <div className="relative z-10 mx-auto max-w-7xl px-4 py-12 sm:px-6 sm:py-24 lg:px-8 lg:py-36">
          <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }} className="max-w-3xl">
            <motion.p initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }}
              className="mb-4 sm:mb-6 text-[10px] sm:text-sm uppercase tracking-[0.35em] sm:tracking-[0.45em] text-gold font-semibold">Commercial Real Estate Investment</motion.p>
            <h1 className="text-4xl font-semibold leading-[1.08] tracking-tight text-cream sm:text-5xl md:text-6xl lg:text-7xl">
              <span className="luxury-text-gradient">Commercial Real</span>
              <span className="block mt-1 sm:mt-3">Estate</span>
              <span className="block mt-1 sm:mt-3 luxury-text-gradient">Investment.</span>
            </h1>
            <p className="mt-3 sm:mt-4 text-base sm:text-xl md:text-2xl italic text-cream font-light">
              Commercial Real Estate the Way It Should Be Done.
            </p>
            <p className="mt-3 sm:mt-5 text-lg sm:text-xl md:text-2xl text-cream/85 font-medium">
              Texas-Based. Nationally Active.
            </p>
            <p className="mt-4 max-w-2xl text-sm leading-6 text-cream/70 sm:mt-6 sm:text-base sm:leading-8">
              Imaani Capital acquires multifamily, retail, and industrial properties across the United States with primary focus in Texas. We move fast, close with certainty, and build lasting value.
            </p>
            <div className="mt-6 flex flex-col gap-2.5 sm:flex-row sm:flex-wrap sm:mt-10 sm:gap-5">
              <GoldButton onClick={() => navigateTo("sell")} className="!px-5 !py-3 sm:!px-8 sm:!py-4 !text-xs sm:!text-sm w-full sm:w-auto" data-testid="hero-submit-property">
                Submit Your Property <ArrowRight className="h-3.5 w-3.5 ml-1 inline" />
              </GoldButton>
              <GoldButton onClick={() => navigateTo("acquisitions")} variant="secondary" className="!px-5 !py-3 sm:!px-8 sm:!py-4 !text-xs sm:!text-sm w-full sm:w-auto" data-testid="hero-acquisitions">
                Our Acquisition Criteria
              </GoldButton>
            </div>
            <p className="mt-4 sm:mt-6 text-xs sm:text-sm text-cream/50 font-medium">
              Multifamily &middot; Retail &middot; Industrial &middot; Texas &amp; Sun Belt Focus
            </p>
          </motion.div>
        </div>
      </section>

      {/* ═══ CREDIBILITY BAR ═══ */}
      <section className="border-b border-cream-warm bg-cream" data-testid="credibility-bar">
        <div className="mx-auto grid max-w-7xl grid-cols-2 gap-3 px-4 py-8 sm:gap-5 sm:px-6 sm:py-12 md:grid-cols-4 lg:px-8">
          {[
            { value: 50, prefix: "$", suffix: "M+", label: "Capital Reviewed" },
            { value: "Multifamily", label: "Primary Focus" },
            { value: "National", label: "Acquisition Reach" },
            { value: "Texas", label: "Headquarters" },
          ].map((s, i) => (
            <div key={i} className="text-center p-2.5 sm:p-4">
              <div className="text-xl font-bold text-[#0A0A0A] sm:text-3xl md:text-4xl">
                <AnimatedCounter value={s.value} prefix={s.prefix || ""} suffix={s.suffix || ""} />
              </div>
              <div className="mt-1 text-[9px] sm:text-sm uppercase tracking-[0.1em] sm:tracking-[0.2em] text-emerald-700 font-medium leading-tight">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ═══ ABOUT THE FIRM ═══ */}
      <Section eyebrow="About Us" title="About Imaani Capital"
        description="A commercial real estate investment firm built on discipline, market expertise, and faith-driven conviction.">
        <div className="grid gap-8 lg:grid-cols-2 lg:gap-16 items-start">
          <div>
            <p className="text-sm sm:text-base text-[#555] leading-relaxed">
              We are a commercial real estate investment firm specializing in the acquisition of multifamily, retail, and industrial assets across the United States. Headquartered in Houston, Texas, our team combines market expertise, proprietary deal flow, and AI-powered underwriting to identify and execute on value-add opportunities.
            </p>
            <p className="mt-4 text-sm sm:text-base text-[#555] leading-relaxed">
              We partner with sellers who need certainty, investors who seek returns, and brokers who need a reliable buyer.
            </p>
            <button onClick={() => navigateTo("about")} data-testid="about-learn-more"
              className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-emerald-700 hover:text-emerald-600 transition-colors">
              Learn More About Our Firm <ArrowRight className="h-4 w-4" />
            </button>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {[
              { icon: Building2, title: "Multifamily Focus", desc: "Our primary asset class" },
              { icon: MapPin, title: "National Reach", desc: "Acquisitions in all 50 states" },
              { icon: Shield, title: "Texas HQ", desc: "Houston-based team" },
              { icon: Clock, title: "48-Hour Offers", desc: "Fast, decisive execution" },
            ].map((s, i) => (
              <Card key={i} className="!p-5">
                <s.icon className="h-5 w-5 text-gold mb-3" />
                <h3 className="text-sm font-bold text-[#0A0A0A]">{s.title}</h3>
                <p className="mt-1 text-xs text-[#888]">{s.desc}</p>
              </Card>
            ))}
          </div>
        </div>
      </Section>

      {/* ═══ WHAT WE ACQUIRE ═══ */}
      <section className="relative py-16 sm:py-24 overflow-hidden" data-testid="acquisitions-section">
        <div className="absolute inset-0 bg-gradient-to-b from-emerald-950 via-[#022c22] to-emerald-950" />
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M20 0L40 20L20 40L0 20z' fill='none' stroke='%23E5C98B' stroke-width='0.5'/%3E%3C/svg%3E")`, backgroundSize: '40px 40px' }} />
        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeUp} className="mb-8 sm:mb-12 max-w-3xl">
            <p className="text-xs sm:text-sm uppercase tracking-[0.4em] text-gold font-semibold">What We Buy</p>
            <h2 className="mt-3 text-3xl font-bold tracking-tight text-cream sm:mt-4 sm:text-4xl md:text-5xl">Our Acquisition Criteria</h2>
            <p className="mt-3 text-base leading-7 text-cream/70 sm:mt-4 sm:text-lg">
              We acquire commercial real estate assets nationally with primary focus in Texas and the Sun Belt.
            </p>
          </motion.div>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {ACQUISITION_TYPES.map((a, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }}
                className="rounded-2xl border border-emerald-700/30 bg-emerald-900/30 backdrop-blur-sm p-6 sm:p-8 hover:border-gold/30 transition-colors">
                <div className="w-10 h-10 rounded-xl bg-emerald-800/50 border border-emerald-700/50 flex items-center justify-center mb-4">
                  <a.icon className="h-5 w-5 text-gold" />
                </div>
                <h3 className="text-lg font-bold text-cream mb-2">{a.title}</h3>
                <p className="text-sm text-cream/70 leading-relaxed">{a.desc}</p>
              </motion.div>
            ))}
          </div>

          <motion.div {...fadeUp} className="mt-10 flex flex-wrap justify-center gap-3 sm:gap-0 sm:divide-x sm:divide-emerald-700/40">
            {[
              "Deal Size $500K\u2014$50M",
              "Direct Purchase, Assignment, JV",
              "Close in 21 Days",
              "National with Texas Focus",
            ].map((t, i) => (
              <span key={i} className="px-4 sm:px-6 text-xs sm:text-sm font-semibold text-gold/80 whitespace-nowrap">{t}</span>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ═══ PRIMARY MARKETS — INTERACTIVE MAP ═══ */}
      <TexasMarketsMap onSubmit={() => navigateTo("sell")} />

      {/* ═══ HOW WE WORK ═══ */}
      <section className="relative py-16 sm:py-24 overflow-hidden" data-testid="how-we-work">
        <div className="absolute inset-0 bg-gradient-to-b from-emerald-950 via-[#022c22] to-emerald-950" />
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M20 0L40 20L20 40L0 20z' fill='none' stroke='%23E5C98B' stroke-width='0.5'/%3E%3C/svg%3E")`, backgroundSize: '40px 40px' }} />
        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeUp} className="mb-8 sm:mb-12 text-center">
            <p className="text-xs sm:text-sm uppercase tracking-[0.4em] text-gold font-semibold">How It Works</p>
            <h2 className="mt-3 text-3xl font-bold tracking-tight text-cream sm:mt-4 sm:text-4xl md:text-5xl">Two Ways to Work With Imaani Capital</h2>
          </motion.div>

          <div className="grid gap-6 lg:grid-cols-2">
            {/* Sellers Track */}
            <motion.div {...fadeUp} className="rounded-2xl border border-emerald-700/30 bg-emerald-900/30 backdrop-blur-sm p-6 sm:p-8">
              <div className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.3em] text-gold mb-3">For Property Sellers</div>
              <div className="h-0.5 w-10 bg-gold/50 mb-6" />
              {[
                "Submit your property details online or call us directly.",
                "Receive a no-obligation offer within 48 hours.",
                "Close in as little as 21 days with no fees or commissions.",
              ].map((step, i) => (
                <div key={i} className="flex items-start gap-4 mb-5">
                  <span className="w-7 h-7 rounded-full bg-gold/20 text-gold text-xs font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                  <p className="text-sm text-cream/80 leading-relaxed pt-1">{step}</p>
                </div>
              ))}
              <GoldButton onClick={() => navigateTo("sell")} className="!w-full !py-3 !text-xs sm:!text-sm" data-testid="sellers-cta">
                Submit Your Property <ArrowRight className="h-3.5 w-3.5 ml-1 inline" />
              </GoldButton>
            </motion.div>

            {/* Capital Partners Track */}
            <motion.div {...fadeUp} className="rounded-2xl border border-emerald-700/30 bg-emerald-900/30 backdrop-blur-sm p-6 sm:p-8">
              <div className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.3em] text-gold mb-3">For Capital Partners</div>
              <div className="h-0.5 w-10 bg-gold/50 mb-6" />
              {[
                "Join our investor and capital partner network.",
                "Review curated acquisition opportunities.",
                "Deploy capital alongside Imaani Capital in Texas and beyond.",
              ].map((step, i) => (
                <div key={i} className="flex items-start gap-4 mb-5">
                  <span className="w-7 h-7 rounded-full bg-gold/20 text-gold text-xs font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                  <p className="text-sm text-cream/80 leading-relaxed pt-1">{step}</p>
                </div>
              ))}
              <GoldButton onClick={() => navigateTo("investors")} className="!w-full !py-3 !text-xs sm:!text-sm" data-testid="partners-cta">
                Become a Capital Partner <ArrowRight className="h-3.5 w-3.5 ml-1 inline" />
              </GoldButton>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══ SUBMIT A PROPERTY ═══ */}
      <SubmitPropertySection />

      {/* ═══ TESTIMONIALS ═══ */}
      <Section eyebrow="Testimonials" title="What People Say"
        description="From sellers, brokers, and capital partners across Texas and beyond.">
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {TESTIMONIALS.map((t, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}>
              <Card className="h-full !p-6">
                <div className="flex gap-0.5 mb-4">
                  {[...Array(5)].map((_, j) => <Star key={j} className="h-3.5 w-3.5 fill-gold text-gold" />)}
                </div>
                <p className="text-sm text-[#555] leading-relaxed italic mb-5">"{t.quote}"</p>
                <div className="mt-auto">
                  <p className="text-sm font-bold text-[#0A0A0A]">{t.name}</p>
                  <p className="text-xs text-[#999]">{t.role}</p>
                </div>
              </Card>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* ═══ FINAL CTA ═══ */}
      <section className="mx-auto max-w-7xl px-4 pb-16 sm:px-6 sm:pb-28 lg:px-8" data-testid="contact-strip">
        <motion.div {...fadeUp} className="relative rounded-2xl sm:rounded-[3rem] overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-gold/40 via-emerald-500/25 to-gold/30 p-[2px] rounded-2xl sm:rounded-[3rem]">
            <div className="absolute inset-[2px] rounded-[calc(1rem-2px)] sm:rounded-[calc(3rem-2px)] bg-gradient-to-br from-[#03352a] via-[#022c22] to-[#044332]" />
          </div>
          <div className="relative p-6 sm:p-10 md:p-16 text-center">
            <p className="text-xs sm:text-sm uppercase tracking-[0.4em] text-gold/70 mb-3 sm:mb-4">Get Started</p>
            <h2 className="text-2xl font-semibold tracking-tight text-cream sm:text-3xl md:text-5xl luxury-text-gradient">Ready to Talk?</h2>
            <p className="mt-4 max-w-2xl mx-auto text-base leading-7 text-cream-soft sm:mt-6 sm:text-lg sm:leading-8">
              Whether you have a property to sell or capital to deploy, we'd like to hear from you.
            </p>
            <div className="mt-6 flex flex-wrap gap-3 justify-center sm:mt-10 sm:gap-5">
              <GoldButton onClick={() => navigateTo("sell")} className="!px-5 !py-3 sm:!px-8 sm:!py-4 !text-xs sm:!text-sm" data-testid="final-submit-property">Submit a Property</GoldButton>
              <GoldButton onClick={() => navigateTo("contact")} variant="secondary" className="!px-5 !py-3 sm:!px-8 sm:!py-4 !text-xs sm:!text-sm" data-testid="final-contact-team">Contact Our Team</GoldButton>
            </div>
            <div className="mt-8 flex items-center justify-center gap-2">
              <Mail className="h-4 w-4 text-cream/50" />
              <a href="mailto:connect@imaanicapital.com" className="text-sm text-cream/50 hover:text-gold transition-colors">connect@imaanicapital.com</a>
            </div>
          </div>
        </motion.div>
      </section>
    </>
  );
}


const PROPERTY_TYPES = ["Multifamily", "Retail", "Industrial", "Mixed-Use", "Other"];

function SubmitPropertySection() {
  const [form, setForm] = useState({ property_type: "", address: "", asking_price: "", name: "", phone: "", email: "" });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const API = process.env.REACT_APP_BACKEND_URL;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/sellers/submit`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (res.ok) setSubmitted(true);
    } catch (err) { console.error(err); }
    finally { setSubmitting(false); }
  };

  const inputCls = "w-full px-4 py-3 rounded-xl border border-[#EDE8DC] bg-white text-sm text-[#0A0A0A] placeholder-[#bbb] focus:outline-none focus:border-emerald-400 focus:ring-1 focus:ring-emerald-200 transition-colors";

  return (
    <section className="bg-cream border-y border-cream-warm" data-testid="submit-property-section">
      <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 sm:py-20 lg:px-8">
        <div className="grid gap-10 lg:grid-cols-5 lg:gap-16 items-start">

          {/* Left: copy + trust signals */}
          <motion.div {...fadeUp} className="lg:col-span-2">
            <p className="text-xs sm:text-sm uppercase tracking-[0.4em] text-gold font-semibold mb-3">Sell To Us</p>
            <h2 className="text-2xl font-bold tracking-tight text-[#0A0A0A] sm:text-3xl lg:text-4xl">Submit Your Property</h2>
            <div className="mt-3 h-0.5 w-12 bg-gold/60" />
            <p className="mt-5 text-sm sm:text-base text-[#555] leading-7">
              We buy commercial real estate nationwide. Receive a no-obligation offer within 48 hours.
            </p>
            <div className="mt-8 space-y-4">
              {[
                { icon: DollarSign, text: "No Fees or Commissions" },
                { icon: Clock, text: "48-Hour Written Offer" },
                { icon: CheckCircle, text: "Close in as Few as 21 Days" },
              ].map((t, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-emerald-100 to-emerald-50 border border-emerald-200 flex items-center justify-center shrink-0">
                    <t.icon className="h-4 w-4 text-emerald-700" />
                  </div>
                  <span className="text-sm font-semibold text-[#333]">{t.text}</span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Right: form */}
          <motion.div {...fadeUp} transition={{ delay: 0.1 }} className="lg:col-span-3">
            {submitted ? (
              <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
                className="rounded-2xl border border-emerald-200 bg-white p-8 sm:p-10 text-center shadow-sm">
                <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-5">
                  <CheckCircle className="h-7 w-7 text-emerald-600" />
                </div>
                <h3 className="text-xl font-bold text-[#0A0A0A] mb-2">Submission Received</h3>
                <p className="text-sm text-[#555] leading-relaxed max-w-sm mx-auto">
                  Thank you — we will contact you within 24 hours to discuss your property.
                </p>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit} className="rounded-2xl border border-[#EDE8DC] bg-white p-6 sm:p-8 shadow-[0_2px_20px_rgba(0,0,0,0.03)]" data-testid="homepage-seller-form">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="sm:col-span-2">
                    <label className="block text-xs font-bold uppercase tracking-[0.08em] text-[#888] mb-1.5">Property Type *</label>
                    <select value={form.property_type} onChange={e => setForm({ ...form, property_type: e.target.value })} required className={inputCls} data-testid="hp-property-type">
                      <option value="">Select type</option>
                      {PROPERTY_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-xs font-bold uppercase tracking-[0.08em] text-[#888] mb-1.5">Property Address *</label>
                    <input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} required placeholder="Full property address" className={inputCls} data-testid="hp-address" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-xs font-bold uppercase tracking-[0.08em] text-[#888] mb-1.5">Asking Price</label>
                    <input value={form.asking_price} onChange={e => setForm({ ...form, asking_price: e.target.value })} placeholder="Or select 'Make Me An Offer'" className={inputCls} data-testid="hp-price" />
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-[0.08em] text-[#888] mb-1.5">Your Name *</label>
                    <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required placeholder="Full name" className={inputCls} data-testid="hp-name" />
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-[0.08em] text-[#888] mb-1.5">Phone Number *</label>
                    <input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} required placeholder="(xxx) xxx-xxxx" className={inputCls} data-testid="hp-phone" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-xs font-bold uppercase tracking-[0.08em] text-[#888] mb-1.5">Email *</label>
                    <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required placeholder="you@email.com" className={inputCls} data-testid="hp-email" />
                  </div>
                </div>
                <button type="submit" disabled={submitting} data-testid="hp-submit-btn"
                  className="mt-5 w-full flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl text-sm font-bold text-white transition-all disabled:opacity-50 hover:brightness-110"
                  style={{ background: "linear-gradient(135deg, #1B4332, #2D6A4F)", boxShadow: "0 2px 12px rgba(27,67,50,0.3)" }}>
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Get My Offer <ArrowRight className="h-4 w-4" /></>}
                </button>
                <p className="mt-3 text-center text-[10px] text-[#bbb]">Your information is confidential. We never share your details.</p>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
